import { useState, useEffect, useCallback, useRef } from "react";
import { NotificationManager } from "react-notifications";
import {
  GetSampleTypes,
  GetSampleMatrices,
} from "../../services/SamplesMaintenanceService";
import {
  GetFacilityIds,
  GetSamplingPointIds,
  GetSampleLocations,
  SaveFacilityId,
  SaveSamplingPointId,
} from "../../services/ClientsMaintenanceService";
import { GetTestProcesses } from "../../services/TestProcessService";
import { VALIDATION_PROCESS_TYPE, toOptions } from "./sampleFieldOptions";

// Pulls the API's own message off a ky HTTPError. Returns "" for a network
// failure or a body that is not the usual response payload.
const readApiMessage = async (error) => {
  try {
    const body = await error?.response?.json();
    return body?.message || "";
  } catch {
    return "";
  }
};

const EMPTY_LOOKUPS = {
  sampleTypes: [],
  sampleMatrices: [],
  facilityIds: [],
  samplingPointIds: [],
  sampleLocations: [],
  validationProcesses: [],
  addFacilityId: async () => null,
  addSamplingPointId: async () => null,
};

// Loads the lists behind the sample dropdowns once for a whole samples table,
// so the edit dialog and every expanded row share one set of requests instead
// of each fetching its own. Sample types and matrices come from sample
// maintenance; facility ids, sampling points and locations are scoped to the
// order's client, the same sources the legacy VLP order-entry screen binds to.
//
// Also exposes the quick-add that VLP puts behind the "+" next to Facility Id
// and Sampling Point: the new name is written to the client's list and the
// dropdown is reloaded so it can be picked straight away.
export default function useSampleLookups(accountNumber) {
  const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Maintenance lists are the same for every client, so load them once.
  useEffect(() => {
    const loadMaintenanceLookups = async () => {
      try {
        const [types, matrices, processes] = await Promise.all([
          GetSampleTypes(),
          GetSampleMatrices(),
          // The endpoint skips page * pageSize, so page 0 is the first page.
          GetTestProcesses(0, 1000),
        ]);

        if (!mounted.current) return;

        setLookups((prev) => ({
          ...prev,
          sampleTypes: toOptions(types?.results, "samtype"),
          sampleMatrices: toOptions(matrices?.results, "sampleMatrix"),
          validationProcesses: (processes?.results || [])
            .filter((row) => row?.processtype === VALIDATION_PROCESS_TYPE)
            .map((row) => (row.processId ?? "").toString().trim())
            .filter((value) => value !== ""),
        }));
      } catch (error) {
        if (mounted.current) {
          NotificationManager.warning(
            "Could not load sample type, matrix and process lists"
          );
        }
      }
    };

    loadMaintenanceLookups();
  }, []);

  // The client's facility ids, sampling points and sample locations change with
  // the order, so reload them whenever the account does - and again after a
  // quick-add writes a new one.
  const loadClientLookups = useCallback(async () => {
    if (!accountNumber) {
      setLookups((prev) => ({
        ...prev,
        facilityIds: [],
        samplingPointIds: [],
        sampleLocations: [],
      }));
      return;
    }

    try {
      const [facilities, samplingPoints, locations] = await Promise.all([
        GetFacilityIds(accountNumber),
        GetSamplingPointIds(accountNumber),
        GetSampleLocations(accountNumber),
      ]);

      if (!mounted.current) return;

      setLookups((prev) => ({
        ...prev,
        facilityIds: toOptions(facilities?.results, "name"),
        samplingPointIds: toOptions(samplingPoints?.results, "name"),
        sampleLocations: (locations?.results || [])
          .map((row) => (row?.location ?? "").toString().trim())
          .filter((value) => value !== ""),
      }));
    } catch (error) {
      if (mounted.current) {
        NotificationManager.warning(
          "Could not load this client's facility, sampling point and location lists"
        );
      }
    }
  }, [accountNumber]);

  useEffect(() => {
    loadClientLookups();
  }, [loadClientLookups]);

  // Adds one name to a client-scoped list and returns it so the caller can
  // select it. Returns null when there is nothing to add, and rethrows so the
  // quick-add dialog can stay open on failure.
  const addClientListValue = useCallback(
    async (saveFn, name, errorMessage) => {
      const trimmed = (name || "").toString().trim();
      if (!trimmed || !accountNumber) {
        return null;
      }

      try {
        await saveFn({ accountNumber, name: trimmed });
        await loadClientLookups();
        return trimmed;
      } catch (error) {
        // The save rejects a name the client already has, so show what the
        // API said rather than a generic failure.
        NotificationManager.error(
          (await readApiMessage(error)) || errorMessage
        );
        throw error;
      }
    },
    [accountNumber, loadClientLookups]
  );

  const addFacilityId = useCallback(
    (name) =>
      addClientListValue(SaveFacilityId, name, "Failed to add facility ID"),
    [addClientListValue]
  );

  const addSamplingPointId = useCallback(
    (name) =>
      addClientListValue(
        SaveSamplingPointId,
        name,
        "Failed to add sampling point ID"
      ),
    [addClientListValue]
  );

  return { ...lookups, addFacilityId, addSamplingPointId };
}

export { EMPTY_LOOKUPS };
